import * as React from "react"
import { ThemeCss, Link, Theme } from "gatsby-interface"
import { MdErrorOutline } from "react-icons/md"
import { visuallyHiddenCss } from "@modules/a11y/stylesheets"
import { lighthouseScoreColors } from "@modules/metrics/lighthouse"
import getLighthouseScore from "@modules/metrics/helpers/getLighthouseScore"
import useLighthouseScoresForBuild from "@modules/build/metrics/hooks/useLighthouseScoresForBuild"
import { getSiteDetailsTabBasePath } from "@modules/site/details/utils"
import { sites as text } from "@modules/locales/default.js"
import { FormattedMessage } from "@modules/locales"
import { SiteDetailsTab } from "@modules/site/details/constants"

export type LighthouseScoresProps = {
  siteId: string
  organizationId: string
  buildId: string
  branch: string
}

enum ScoresDisplayState {
  Loading = `LOADING`,
  Error = `ERROR`,
  Empty = `EMPTY`,
  Ready = `READY`,
  Disabled = `DISABLED`,
}

export function LighthouseScores({
  siteId,
  organizationId,
  buildId,
  branch,
}: LighthouseScoresProps) {
  const [
    scores,
    { loading, error, availablePluginsLoading, lighthouseEnabled },
  ] = useLighthouseScoresForBuild(siteId, buildId, branch)

  let displayState: ScoresDisplayState

  if (loading || availablePluginsLoading) {
    displayState = ScoresDisplayState.Loading
  } else if (error) {
    displayState = ScoresDisplayState.Error
  } else if (!lighthouseEnabled) {
    displayState = ScoresDisplayState.Disabled
  } else if (scores.length === 0) {
    displayState = ScoresDisplayState.Empty
  } else {
    displayState = ScoresDisplayState.Ready
  }

  return (
    <div css={rootCss}>
      {displayState === ScoresDisplayState.Loading && (
        <p css={textCss}>{text.messages.loadingScores}</p>
      )}

      {displayState === ScoresDisplayState.Error && (
        <p css={textCss}>
          <MdErrorOutline css={errorIconCss} />{" "}
          {text.messages.somethingWentWrongWhenTryingToLoadTheLighthouseScores}
        </p>
      )}

      {displayState === ScoresDisplayState.Disabled && (
        <p css={textCss}>
          <FormattedMessage<never, "link">
            message={text.messages.reportNotEnabled}
            tags={{
              link: function(content) {
                return (
                  <Link
                    to={`${getSiteDetailsTabBasePath(
                      SiteDetailsTab.Settings,
                      siteId,
                      organizationId
                    )}/reports`}
                  >
                    {content}
                  </Link>
                )
              },
            }}
          />
        </p>
      )}

      {displayState === ScoresDisplayState.Empty && (
        <p css={textCss}>
          <FormattedMessage<never, "link">
            message={text.messages.noReportForSite}
            tags={{
              link: function(content) {
                return (
                  <Link
                    to={`${getSiteDetailsTabBasePath(
                      SiteDetailsTab.Settings,
                      siteId,
                      organizationId
                    )}/reports`}
                  >
                    {content}
                  </Link>
                )
              },
            }}
          />
        </p>
      )}

      {displayState === ScoresDisplayState.Ready && (
        <React.Fragment>
          <div css={metricsCss}>
            {scores.map(([label, value]: [string, number]) => {
              const color = lighthouseScoreColors[getLighthouseScore(value)]

              return (
                <React.Fragment key={label}>
                  <span css={visuallyHiddenCss}>
                    {`Metric ${label} is ${value} on 100`}
                  </span>
                  <span aria-hidden={true} css={metricCss}>
                    <span css={textCss}>{label}</span>
                    <span
                      css={(theme: Theme) => [
                        dotCss(theme),
                        {
                          background: color,
                        },
                      ]}
                    />
                    <strong>{value}</strong>
                  </span>
                </React.Fragment>
              )
            })}
          </div>
          <Link
            href={`https://build-${buildId}${process.env.GATSBY_PREVIEW_DOMAIN}/reports/lighthouse/index.html`}
            variant="SIMPLE"
            css={{
              marginLeft: `auto`,
            }}
          >
            {text.actions.viewFullLighthouseReport}
          </Link>
        </React.Fragment>
      )}
    </div>
  )
}

/* styles */

const rootCss: ThemeCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  fontSize: theme.fontSizes[1],
  width: `100%`,
})

const textCss: ThemeCss = theme => ({
  padding: 0,
  margin: 0,
  color: theme.colors.grey[50],
})

const errorIconCss: ThemeCss = theme => ({
  color: theme.colors.red[70],
  verticalAlign: `middle`,
})

const metricsCss: ThemeCss = theme => ({
  display: `flex`,
  flexWrap: `wrap`,
})

const metricCss: ThemeCss = theme => ({
  display: `flex`,
  alignItems: `center`,
  flexShrink: 0,
  marginRight: theme.space[7],

  strong: {
    color: theme.colors.grey[60],
  },
})

const dotCss: ThemeCss = theme => ({
  width: `10px`,
  height: `10px`,
  borderRadius: theme.radii[5],
  background: `black`,
  marginRight: theme.space[2],
  marginLeft: theme.space[4],
})
